"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "cn";
import type * as React from "react";
import {
  keepLayerOpenForPopup,
  PortalContainerContext,
  usePortalContainerRef,
} from "./PortalContainer";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The modal panel. Popups opened inside it, such as a Combobox list, render
 * within it so they keep receiving the mouse while the dialog turns pointer
 * events off outside itself.
 */
function DialogContent({
  className,
  children,
  ref,
  onEscapeKeyDown,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
  const [container, contentRef] = usePortalContainerRef(ref);
  return (
    <DialogPortal>
      <DialogOverlay />
      <PortalContainerContext.Provider value={container}>
        <DialogPrimitive.Content
          ref={contentRef}
          data-slot="dialog-content"
          className={cn(
            "fixed top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "flex flex-col w-full max-h-[90dvh]",
            "max-w-[calc(100%-2rem)] sm:max-w-lg",
            "bg-card rounded-lg shadow-xl outline-none",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "duration-200",
            className,
          )}
          {...props}
          onEscapeKeyDown={keepLayerOpenForPopup(container, onEscapeKeyDown)}
        >
          {children}
        </DialogPrimitive.Content>
      </PortalContainerContext.Provider>
    </DialogPortal>
  );
}

interface DialogHeaderProps extends React.ComponentProps<"div"> {
  type?: "header" | "close-only";
}

function DialogHeader({
  className,
  type = "close-only",
  children,
  ...props
}: DialogHeaderProps) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex items-center shrink-0 px-6 py-4",
        type === "header" ? "justify-between" : "justify-end",
        className,
      )}
      {...props}
    >
      {type === "header" && children}
      <DialogPrimitive.Close
        className={cn(
          "flex items-center justify-center size-6 rounded-sm shrink-0 cursor-pointer",
          "text-foreground",
          "hover:bg-accent",
          "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
          "disabled:pointer-events-none",
        )}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </div>
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("heading-md text-foreground", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("body-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

interface DialogFooterProps extends React.ComponentProps<"div"> {
  layout?: "buttons-right" | "full-width";
}

function DialogFooter({
  className,
  layout = "buttons-right",
  ...props
}: DialogFooterProps) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex shrink-0 gap-2",
        "px-6 pb-6 pt-3",
        layout === "buttons-right" ? "justify-end" : "[&>*]:flex-1",
        className,
      )}
      {...props}
    />
  );
}

interface SimpleDialogProps
  extends React.ComponentProps<typeof DialogPrimitive.Root> {
  trigger: React.ReactNode;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  footerLayout?: DialogFooterProps["layout"];
}

function SimpleDialog({
  trigger,
  title,
  children,
  footer,
  footerLayout = "buttons-right",
  ...props
}: SimpleDialogProps) {
  return (
    <Dialog {...props}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader type="header">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className={cn("px-6", !footer && "pb-6")}>{children}</div>
        {footer && <DialogFooter layout={footerLayout}>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  SimpleDialog,
  type DialogFooterProps,
  type DialogHeaderProps,
  type SimpleDialogProps,
};
